import { ComponentProps, forwardRef, MutableRefObject, useEffect } from "react"
import { useSearchInput } from "./useSearchInput"
import { useHydrating, useRoute, useRouter } from "eddev/routing"

type SearchInputProps = ComponentProps<"input"> & {
  resultContainer?: MutableRefObject<HTMLDivElement | null>
  openFullResults?: boolean
}

export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(
  ({ resultContainer, openFullResults, ...props }, ref) => {
    const router = useRouter()
    const route = useRoute()
    const input = useSearchInput()

    const getNav = () => {
      if (!resultContainer?.current) return
      const items = Array.from(resultContainer.current.querySelectorAll("[data-search-item]"))
      const activeIndex = items.findIndex((el) => el.getAttribute("data-search-selected"))
      const element = activeIndex !== -1 ? items[activeIndex] : null
      const link = element
        ? element.tagName === "A" || element.tagName === "BUTTON"
          ? element
          : element.querySelector("[data-main-link]")
        : null

      return {
        items,
        activeIndex,
        element,
        link,
      }
    }

    const navigate = (step: number) => {
      const nav = getNav()
      if (!nav) return
      let nextIndex = nav.activeIndex + step
      if (nextIndex < -1) nextIndex = nav.items.length - 1
      nav.items.forEach((el) => el.removeAttribute("data-search-selected"))
      if (nextIndex >= 0 && nextIndex <= nav.items.length - 1) {
        nav.items[nextIndex].setAttribute("data-search-selected", "true")
      }

      const link = getNav()?.link

      if (link && link.getAttribute("href") && !link.getAttribute("target")) {
        router.preload(link.getAttribute("href")!)
      }

      link?.scrollIntoView({
        behavior: "smooth",
      })
    }

    const navigateDown = () => {
      navigate(1)
    }

    const navigateUp = () => {
      navigate(-1)
    }

    const selectItem = (link: Element) => {
      link.dispatchEvent(
        new MouseEvent("click", {
          view: window,
          bubbles: true,
          cancelable: true,
          buttons: 1,
        }),
      )
    }

    const hydrating = useHydrating()

    useEffect(() => {
      if (hydrating) return
      if (route.view === "search-results") {
        router.replaceQuery({ q: input.searchQuery.trim() })
      }
    }, [input.searchQuery, hydrating])

    return (
      <input
        {...props}
        value={input.inputQuery}
        enterKeyHint="search"
        onKeyDown={(e) => {
          if (e.key === "ArrowDown") {
            e.preventDefault()
            navigateDown()
          } else if (e.key === "ArrowUp") {
            e.preventDefault()
            navigateUp()
          }
          if (e.key === "Enter") {
            const nav = getNav()
            if (nav?.link) {
              selectItem(nav.link)
            } else if (openFullResults) {
              const trimmed = e.currentTarget.value.trim()
              if (trimmed) {
                router.navigate("/search?q=" + encodeURIComponent(trimmed))
              }
            }
          }
        }}
        onKeyUp={(e) => {
          input.update(e.currentTarget.value, e.key)
        }}
        onChange={(e) => {
          input.update(e.currentTarget.value)
        }}
        ref={ref}
        disabled={env.admin}
      />
    )
  },
)
